<script>

import {extend, localize} from "vee-validate/dist/vee-validate.full";
import {getUserLocale} from 'get-user-locale';

    export default {

        name: "questionMixin",

        props: {
            variable: {
                type: Object,
                required: true,
            },
            locale: {
                type: Object,
                required: true,
            },
            translations: {
                type: Object,
                required: true,
            },
            loading: {
                type: Boolean,
                required: false,
                default: false,
            },
            buttonColor: {
                type: String,
                required: true,
            },
            backgroundColor: {
                type: String,
                required: true,
            },
            required: {
                type: Boolean,
                required: false,
                default: null,
            },
            errorMessages: {
                type: String | null,
                required: false,
                default: null,
            },
            backButtonEnabled: {
                type: Boolean,
                required: false,
                default: false,
            },
            saving: {
                type: Boolean,
                required: false,
                default: false,
            }
        },

        data() {
            return {
                isValid: false,
                model: null,
                answerRequired: false,
                optionsColumnOne: [],
                optionsColumnTwo: [],
                twoColumns: false,
                innerHeight: null,
                innerWidth: null,
            }
        },

        computed: {

            questionText: {
                get() {
                    if (this.variable && this.locale) {

                        let locale = this.findLocale();

                        if (locale) {

                            return locale.question_text;

                        } else {

                            const message = "Could not find question locale! Locale ID" +
                                "(" + this.locale.code + ") for data variable (" + this.variable.name + ") with ID " +
                                "(" + this.variable.id + ")";

                            throw new Error(message);
                        }
                    }
                }
            },

            rules() {
                let rules = {};

                if (this.variable.dynamic_profiling_validators) {

                    this.variable.dynamic_profiling_validators.forEach(validator => {

                            if (validator.type === "required") {
                                this.answerRequired = true;
                            }

                            // TODO: Implement factory with TS
                            if (typeof this[validator.type + 'Validator'] === "function") {
                                this[validator.type + 'Validator'](validator, rules);
                            } else {
                                throw new Error("Validator type " + validator.type + " does not exist!");
                            }
                        }
                    )
                }

                if (this.required) {
                    this.requiredValidator(null, rules);
                } else {
                    this.filledValidator(null, rules)
                }

                return rules;
            },

            counterSize() {

                if (!this.variable.dynamic_profiling_validators) return null;

                const found = this.variable.dynamic_profiling_validators.find(validator => validator.type === 'max');

                if (!found) return null;

                return found.param1;
            },

            isSmallScreen() {
                return this.innerWidth <= 960;
            }
        },

        watch: {

            value(value) {
                this.model = value;
            },

            locale(locale) {
                this.loadLocale(locale.language_code)
            }
        },

        mounted() {
            this.model = this.value;
            this.answerRequired = this.required;
            this.loadLocale(this.locale.language_code)

            // Focus automatically if question ref exists
            this.$nextTick(() => {
                if (this.$refs.question) this.$refs.question.focus();
            });

            this.handleResize();

            window.addEventListener('resize', () => {
                this.handleResize();
            });

            this.splitOptionColumns();
        },

        created() {
            extend('filled', {
                validate: (value,{bool}) => {
                    if (!value) return false;
                    if (value.length === 0) return false;
                    return true;
                },
                computesRequired: true,
                params: ['bool'],
                message: this.translations.survey_answer_not_provided
            });
        },

        methods: {

            handleResize() {
                this.innerWidth = window.innerWidth;
                this.innerHeight = window.innerHeight;

                this.splitOptionColumns();
            },

            splitOptionColumns()
            {
                if (this.variable && this.variable.options) {

                    let options = this.variable.options.sort((a, b) => {
                        return a.order - b.order;
                    });

                    if (!this.isSmallScreen && this.variable.options.length >= 8) {

                        const longOptions = options
                            .map(option => option.locales[0].option_text.length)
                            .filter(len => len > 50);

                        // Long options detected keep in one column
                        if (longOptions.length > 0) {
                            this.optionsColumnOne = [...this.variable.options];
                            return;
                        }

                        // Split options in two columns
                        const count = this.variable.options.length;

                        const sliceSizeColumnOne = Math.ceil(count / 2);
                        const sliceSizeColumnTwo = count - sliceSizeColumnOne;

                        this.optionsColumnOne = options.slice(0, sliceSizeColumnOne);
                        this.optionsColumnTwo = options.slice(sliceSizeColumnTwo * -1);

                        this.twoColumns = true;

                    } else {

                        this.optionsColumnOne = [...options];
                        this.twoColumns = false;
                    }
                }
            },

            requiredValidator(validator, rules) {
                rules.required = true;
            },

            betweenValidator(validator, rules) {
                rules.between = {min: validator.param1, max: validator.param2};
            },

            regexValidator(validator, rules) {
                rules.regex = new RegExp(validator.param1);
            },

            integerValidator(validator, rules) {
                rules.integer = true;
            },

            minValidator(validator, rules) {
                rules.min = parseInt(validator.param1);
            },

            maxValidator(validator, rules) {
                rules.max = parseInt(validator.param1);
            },

            min_dateValidator(validator, rules) {
                rules.min_date = validator.param1;
            },

            max_dateValidator(validator, rules) {
                rules.max_date = validator.param1;
            },

            alphaValidator(validator, rules) {
                rules.alpha = true;
            },

            alpha_numValidator(validator, rules) {
                rules.alpha_num = true;
            },

            numericValidator(validator, rules) {
                rules.numeric = true;
            },

            min_valueValidator(validator, rules) {
                rules.min_value = validator.param1;
            },

            max_valueValidator(validator, rules) {
                rules.max_value = validator.param1;
            },

            emailValidator(validator, rules) {
                rules.email = true;
            },

            min_checksValidator(validator, rules) {
                rules.min_checks = validator.param1;
            },

            max_checksValidator(validator, rules) {
                rules.max_checks = validator.param1;
            },

            filledValidator(validator, rules) {
                rules.filled = true;
            },

            nextClicked() {

                if (this.$refs.validationObserver) {
                    this.$refs.validationObserver.validate()
                        .then(isValid => {
                            if (isValid) {
                                this.$emit('input', this.model);
                            }
                        });
                }
            },

            skipClicked() {
                this.$emit('skip');
            },

            backClicked() {
                this.$emit('back');
            },

            findLocale() {
                if (this.variable && this.locale) {
                    return this.variable.locales.find((locale) => locale.locale_id === this.locale.id);
                }
            },

            loadLocale(code) {
                return import(`vee-validate/dist/locale/${code}.json`).then(locale => {

                    // Insert custom translations

                    //console.log(locale);

                    // locale = Object.assign(locale.messages, {
                    //     required: this.translations.validation_field_required,
                    //     email: this.translations.validation_invalid_email_address
                    // });

                    localize(code, locale);
                });
            },

            findOptionText(option) {
                return option.locales.find(locale => locale.locale_id === this.locale.id).option_text;
            },

            userLocale() {
                return getUserLocale();
            }
        },
    }
</script>
